import React, { useState, useEffect } from 'react';
import { useMembership } from '../../context/MembershipContext';
import membershipService from '../../api/membershipService';
import { FaCrown, FaCheck, FaTimes, FaStar, FaCreditCard, FaUser, FaArrowUp, FaShieldAlt, FaInfinity, FaHeadset } from 'react-icons/fa';
import { alertaGeneral } from '../../utils/alertas';
import { openEpaycoCheckout } from '../../utils/epaycoCheckout';
import MembresiaPrestadorPlanModal from '../ePayco/MembresiaPrestadorPlanModal';
import { getAndValidateToken } from '../../utils/tokenValidation';

const UpgradeToPremiumModal = ({ isOpen, onClose, onUpgradeSuccess }) => {
  const { 
    membershipStatus, 
    upgradePrice, 
    loading, 
    error, 
    canUpgrade,
    isFree
  } = useMembership();
  
  const [upgrading, setUpgrading] = useState(false);
  const [modalPrestadorOpen, setModalPrestadorOpen] = useState(false);
  const [doctorId, setDoctorId] = useState(null);

  useEffect(() => {
    // El script se carga bajo demanda al abrir ePayco
  }, []);

  const handleUpgrade = async () => {
    if (!canUpgrade()) {
      alertaGeneral({
        titulo: 'No disponible',
        messageHtml: 'No puedes activar la membresía premium en este momento',
        isWarning: true
      });
      return;
    }

    setUpgrading(true);
    try {
      const tokenValidation = getAndValidateToken();
      if (!tokenValidation.isValid) {
        throw new Error(tokenValidation.error || 'Sesión inválida');
      }

      const { decoded } = tokenValidation;
      const userId = decoded.id || decoded.userId;

      if (!userId) {
        throw new Error('No se pudo obtener el ID del usuario');
      }

      if (decoded.role === 'doctor') {
        const docId = decoded.doctorId || localStorage.getItem('doctorId');
        if (!docId) {
          throw new Error('No se encontró el perfil del profesional');
        }
        setDoctorId(docId);
        setModalPrestadorOpen(true);
        onClose?.();
        return;
      }

      const result = await membershipService.initiateUpgrade(userId, upgradePrice?.valor);
      
      if (result.ok) {
        await openEpaycoCheckout(result.data);
        onUpgradeSuccess?.();
      } else {
        alertaGeneral({
          titulo: 'Error',
          messageHtml: result.error || 'Error al iniciar el pago',
          isError: true
        });
      }
    } catch (error) {
      alertaGeneral({
        titulo: 'Error',
        messageHtml: error.response?.data?.message || error.message || 'Ocurrió un error al procesar el pago',
        isError: true
      });
    } finally {
      setUpgrading(false);
    }
  };

  if (!isOpen) {
    return (
      <MembresiaPrestadorPlanModal
        isOpen={modalPrestadorOpen}
        onClose={() => setModalPrestadorOpen(false)}
        doctorId={doctorId}
      />
    );
  }

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2147483640] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FaCrown className="text-yellow-500 text-2xl mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Membresía Paciente Premium</h2>
              <p className="text-gray-600">Mismo precio que en el registro — activa todos los beneficios</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando información...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-4">
                <FaTimes className="text-4xl mx-auto" />
              </div>
              <p className="text-red-600">Error al cargar la información</p>
              <p className="text-gray-600 text-sm mt-2">{error}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {/* Plan Actual */}
              <div className="border-2 border-gray-200 rounded-lg p-6">
                <div className="text-center mb-4">
                  <FaUser className="text-4xl text-gray-400 mx-auto mb-2" />
                  <h3 className="text-xl font-semibold text-gray-900">Plan Actual</h3>
                  <p className="text-gray-600">Plan Gratuito</p>
                </div>
                
                <div className="text-center mb-6">
                  <span className="text-3xl font-bold text-gray-900">$0</span>
                  <span className="text-gray-600">/mes</span>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Acceso básico a la plataforma</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Consultas limitadas</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Soporte básico</span>
                  </li>
                </ul>

                <div className="text-center">
                  <span className="inline-block bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                    Plan Actual
                  </span>
                </div>
              </div>

              {/* Plan Premium */}
              <div className="border-2 border-yellow-500 rounded-lg p-6 relative bg-gradient-to-br from-yellow-50 to-orange-50">
                {/* Popular Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-yellow-500 text-white px-4 py-1 rounded-full text-xs font-medium flex items-center">
                    <FaStar className="mr-1" />
                    Recomendado
                  </span>
                </div>

                <div className="text-center mb-4">
                  <FaCrown className="text-4xl text-yellow-500 mx-auto mb-2" />
                  <h3 className="text-xl font-semibold text-gray-900">Plan Premium</h3>
                  <p className="text-gray-600">Acceso completo y exclusivo</p>
                </div>
                
                <div className="text-center mb-6">
                  {upgradePrice ? (
                    <>
                      <span className="text-3xl font-bold text-gray-900">
                        ${upgradePrice.valor?.toLocaleString()}
                      </span>
                      <span className="text-gray-600">/upgrade único</span>
                    </>
                  ) : (
                    <span className="text-gray-500">Cargando precio...</span>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Acceso ilimitado a todas las funciones</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Consultas sin límites</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Soporte prioritario 24/7</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Características exclusivas</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Sin publicidad</span>
                  </li>
                </ul>

                <button
                  onClick={handleUpgrade}
                  disabled={upgrading || !canUpgrade()}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                    upgrading || !canUpgrade()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-500 text-white hover:bg-yellow-600'
                  }`}
                >
                  {upgrading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FaArrowUp className="mr-2" />
                      Upgrade Ahora
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Información adicional */}
          {upgradePrice && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                  <FaShieldAlt className="mr-2" />
                  Información del Upgrade
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-blue-800">
                      <strong>Precio:</strong> ${upgradePrice.valor?.toLocaleString()} COP
                    </p>
                    <p className="text-blue-700">
                      <strong>Tipo:</strong> Upgrade único
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-800">
                      <strong>Descripción:</strong> {upgradePrice.descripcion}
                    </p>
                    <p className="text-blue-700">
                      <strong>Última actualización:</strong> {new Date(upgradePrice.ultimaEdicion).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <p>¿Necesitas ayuda? <span className="font-medium">Contacta soporte</span></p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                {canUpgrade() && (
                  <button
                    onClick={handleUpgrade}
                    disabled={upgrading}
                    className="flex items-center px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                  >
                    {upgrading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <FaCreditCard className="mr-2" />
                        Pagar Membresía Premium
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <MembresiaPrestadorPlanModal
      isOpen={modalPrestadorOpen}
      onClose={() => setModalPrestadorOpen(false)}
      doctorId={doctorId}
    />
    </>
  );
};

export default UpgradeToPremiumModal; 